import { LogService } from "@bitwarden/common/src/abstractions/log.service";
import { Valuation } from "../../../data/valuation.data";
import { TransactionDirection } from "../../../enum/transactionDirection";
import { NormalizationProperties } from "../../../types/normalization-types";
import { SymbolConversion } from "../../../../services/DataCalculationService/symbol/symbolConversion";
import { TransactionView } from "../transaction.view";

import { ReferenceDataView } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";

export class NormalizeTransaction {
  _baseCurrency: string;
  symbolConversion: SymbolConversion;
  protected logger: LogService;
  referenceData: ReferenceDataView[];

  constructor(logger: LogService, baseCurrency: string, referenceData: ReferenceDataView[]) {
    // NOTE: we can't inject the logger if we are using this class in webworkers
    this.logger = logger; // todo should be able to use inject now
    this.referenceData = referenceData;
    this._baseCurrency = baseCurrency;
  }

  // <editor-fold desc="Public Functions">
  /**
   * Normalizes a transaction to the base currency
   * @param transaction
   */
  getNormalizedProperties(transaction: TransactionView): NormalizationProperties {
    if (!transaction.quantity) {
      throw new Error("Transaction Gloss Quantity was not set");
    }
    /*
    if (!transaction.c_balance) {
      throw new Error("Transaction Balance object was not set");
    }
     */

    // the transaction currency is different from the required base currency
    // but a conversion rate and conversion symbol are already supplied on the transaction
    if (
      transaction.quantity.convrate &&
      transaction.quantity.convsym &&
      transaction.quantity.convsym === this._baseCurrency
    ) {
      const normalized = this.normalizeWithSuppliedConvRateSymbol(transaction);
      return normalized;
    }

    // if the transaction currency is the same as the base currency
    if (transaction.quantity.currency === this._baseCurrency) {
      // if the symbol is the currency then no conversion is required
      const normalized = this.normaliseCurrencyEqualBase(transaction);
      return normalized;
    }

    if (transaction?.valuationPrice) {
      const normalized = this.normalizeWithValuationPrice(transaction);
      return normalized;
    }

    if (transaction.quantity.actualQuantity.symbol) {
      const normalized = this.normalizeWithSymbol(transaction);
      return normalized;
    }

    // default to returning the quantity
    let quantity = transaction.quantity.actualQuantity.amount;
    quantity = transaction.direction === TransactionDirection.Out ? -1 * quantity : quantity;
    const normalizedProperties: NormalizationProperties = {
      value: quantity,
      normalizedValue: quantity,
      normalizedCurrency: this._baseCurrency,
    };

    return normalizedProperties;
  }

  // </editor-fold desc="Public Functions">

  // <editor-fold desc="Private Functions">
  private normalizeWithReferenceData(
    quantity: number,
    date: Date,
    symbol: string,
  ): NormalizationProperties {
    if (!this.symbolConversion) {
      this.symbolConversion = new SymbolConversion(
        this._baseCurrency,
        this.logger,
        this.referenceData,
      );
    }

    return this.symbolConversion.normalizeQuantity(quantity, date, symbol);
  }

  /**
   * If the base we need and the currency of the transaction is already the same as the required base
   * we want to normalise to
   * @param transaction
   * @private
   */
  private normaliseCurrencyEqualBase(transaction: TransactionView): NormalizationProperties {
    let quantity = transaction.quantity.actualQuantity.amount;

    quantity = transaction.direction === TransactionDirection.Out ? -1 * quantity : quantity;

    let normalizedProperties: NormalizationProperties = {
      value: quantity,
      normalizedValue: quantity,
      normalizedCurrency: this._baseCurrency,
    };

    // if the symbol is the currency then no conversion is required
    if (transaction.quantity.actualQuantity.symbol === this._baseCurrency) {
      normalizedProperties.normalizingRate = 1;
      return normalizedProperties;
    }

    // if a valuation price was supplied on import then we only need to multiply the two
    if (transaction?.valuationPrice) {
      // multiply the symbol to the price if one is supplied
      normalizedProperties = {
        value: quantity * transaction.valuationPrice,
        symbolValue: transaction.valuationPrice,
        normalizedValue: quantity * transaction.valuationPrice,
        normalizingRate: 1,
        normalizedCurrency: this._baseCurrency,
      };
      return normalizedProperties;
    }

    // if there is a symbol, then we need to find the price of the symbol on the day
    if (quantity && transaction.quantity.actualQuantity.symbol) {
      // normalise the symbol with the current rate because a price was not supplied
      const normalisedRates = this.normalizeWithReferenceData(
        quantity,
        transaction.postDate,
        transaction.quantity.actualQuantity.symbol,
      );
      Object.assign(normalizedProperties, normalisedRates);
      return normalizedProperties;
    }
    return normalizedProperties;
  }

  private normalizeWithSuppliedConvRateSymbol(
    transaction: TransactionView,
  ): NormalizationProperties {
    let value = transaction.quantity.actualQuantity.amount;

    value = transaction.direction === TransactionDirection.Out ? -1 * value : value;

    let normalizedProperties: NormalizationProperties = {
      value: value,
      normalizedValue: value,
      normalizedCurrency: this._baseCurrency,
    };
    let valueCurrencyOverride;

    if (transaction?.valuationPrice) {
      // we need to use the supplied conversion rate to work out the value
      value = value * transaction.valuationPrice;
      normalizedProperties.value = value;
      normalizedProperties.normalizedValue = value;
      normalizedProperties.symbolValue = transaction.valuationPrice;
      normalizedProperties.valueCurrency = transaction.quantity.currency;
      valueCurrencyOverride = transaction.quantity.currency;
    }

    if (transaction.quantity.currency === this._baseCurrency) {
      // if the currency supplied already matches the base, then no conversion is required
      normalizedProperties.normalizingRate = 1;
      return normalizedProperties;
    }

    if (transaction.quantity.convrate) {
      // we need to use the supplied conversion rate to work out the value
      value = value / transaction.quantity.convrate;
      normalizedProperties.valueCurrency = transaction.quantity.convsym;
      valueCurrencyOverride = transaction.quantity.convsym;
      normalizedProperties.normalizedValue = value;
    }

    if (transaction.quantity.convsym === this._baseCurrency) {
      // TODO: Check with Kev if the convrate should be flipped for storing or should we
      // TODO: store the normalizingRate as 1/convrate to keep the system consistent
      normalizedProperties.normalizingRate = transaction.quantity.convrate;
      normalizedProperties.valueCurrency = transaction.quantity.currency;
      return normalizedProperties;
    }

    // normalize the value using the conversion symbol and reference data because it is not in the base currency
    const normalized = this.normalizeWithReferenceData(
      value,
      transaction.postDate,
      transaction.quantity.convsym,
    );
    normalizedProperties = Object.assign(normalizedProperties, normalized);
    if (valueCurrencyOverride) {
      normalizedProperties.valueCurrency = valueCurrencyOverride;
    }
    return normalizedProperties;
  }

  private normalizeWithValuationPrice(transaction: TransactionView): NormalizationProperties {
    const quantity = transaction.quantity.actualQuantity.amount;
    let value = quantity * transaction.valuationPrice;

    value = transaction.direction === TransactionDirection.Out ? -1 * value : value;

    const normalizedProperties: NormalizationProperties = {
      value: value,
      normalizedValue: value,
      symbolValue: transaction.valuationPrice,
      normalizedCurrency: this._baseCurrency,
    };

    if (transaction.quantity.currency) {
      const normalizedRates = this.normalizeWithReferenceData(
        normalizedProperties.normalizedValue,
        transaction.postDate,
        transaction.quantity.currency,
      );
      Object.assign(normalizedProperties, normalizedRates);
      return normalizedProperties;
    }
    return normalizedProperties;
  }

  private normalizeWithSymbol(transaction: TransactionView): NormalizationProperties {
    let value = transaction.quantity.actualQuantity.amount;

    value = transaction.direction === TransactionDirection.Out ? -1 * value : value;

    const normalized = this.normalizeWithReferenceData(
      value,
      transaction.postDate,
      transaction.quantity.actualQuantity.symbol,
    );
    return normalized;
  }

  normalizeImportedTransaction(transaction: TransactionView): void {
    const normalizedProperties = this.getNormalizedProperties(transaction);

    this.setValuationOnTransaction(transaction, normalizedProperties);

    transaction.c_allocationsNormalized = AllocationUtils.createNestedAllocationsWithNewAmount(
      transaction.allocations,
      transaction.c_valuation.normalizedValue.amount,
    );

    return;
  }

  private setValuationOnTransaction(
    transaction: TransactionView,
    normalizedProperties: NormalizationProperties,
  ) {
    if (normalizedProperties) {
      if (transaction.c_valuation instanceof Valuation) {
        transaction.c_valuation.setToValuationObj(normalizedProperties);
      } else {
        transaction.c_valuation = new Valuation().setToValuationObj(normalizedProperties);
      }
    }
  }

  // </editor-fold desc="Public Functions">
}
